import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import {
    Calendar, TrendingUp, TrendingDown, DollarSign,
    Eye, Percent, Clock, Zap, RefreshCw, Minus, BarChart3,
    MapPin, Layers, Tag, ChevronLeft, ChevronRight
} from 'lucide-react';
import {
    LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
    ResponsiveContainer, ReferenceLine
} from 'recharts';
import { getRuleReport } from '@/services/api';
import type { PriceRule } from '@/types/price-rules';

interface Props {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    rule: PriceRule | null;
}

interface HourlyData {
    hour: number;
    hour_label: string;
    impressions: number;
    revenue: number;
    ecpm: number;
    pmr: number;
    clicks: number;
    ctr: number;
}

interface LogEntry {
    id: number;
    action: string;
    source: string;
    previous_price: number | null;
    new_price: number | null;
    metadata: {
        pmr?: number;
        pmr_target?: number;
        pmr_difference?: number;
        ecpm?: number;
        impressions?: number;
        scale_percentage?: number;
        direction?: string;
    };
    message: string;
    created_at: string;
    hour: number;
}

interface ReportData {
    rule: any;
    period: { start: string; end: string };
    timeline: any[];
    hourly_data: HourlyData[];
    logs: LogEntry[];
    summary: {
        total_impressions: number;
        total_revenue: number;
        total_clicks: number;
        avg_ecpm: number;
        avg_pmr: number;
        avg_ctr: number;
        hours_with_data: number;
        price_changes: number;
        scale_ups: number;
        scale_downs: number;
        min_price: number;
        max_price: number;
    };
}

type ViewMode = 'hourly' | 'cumulative';

const METRICS = [
    { key: 'impressions', label: 'Impressões', color: '#3b82f6', yAxisId: 'left' },
    { key: 'revenue', label: 'Receita', color: '#f59e0b', yAxisId: 'right' },
    { key: 'ecpm', label: 'eCPM', color: '#10b981', yAxisId: 'right' },
    { key: 'pmr', label: 'PMR', color: '#8b5cf6', yAxisId: 'percent' },
    { key: 'clicks', label: 'Clicks', color: '#ef4444', yAxisId: 'left' },
    { key: 'ctr', label: 'CTR', color: '#06b6d4', yAxisId: 'percent' },
] as const;

export default function RuleReportModal({ open, onOpenChange, rule }: Props) {
    const [selectedDate, setSelectedDate] = useState(() => {
        const yesterday = new Date();
        yesterday.setDate(yesterday.getDate() - 1);
        return yesterday.toISOString().split('T')[0];
    });
    const [viewMode, setViewMode] = useState<ViewMode>('hourly');
    const [activeMetrics, setActiveMetrics] = useState<Set<string>>(
        new Set(['impressions', 'revenue', 'pmr'])
    );

    const { data: report, isLoading, refetch } = useQuery<ReportData>({
        queryKey: ['rule-report', rule?.id, selectedDate],
        queryFn: () => getRuleReport(rule!.id, selectedDate, selectedDate),
        enabled: open && !!rule?.id,
    });

    const formatPrice = (price: number) =>
        new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'USD' }).format(price);

    const formatNumber = (num: number) => new Intl.NumberFormat('pt-BR').format(num);

    const changeDate = (days: number) => {
        const date = new Date(selectedDate);
        date.setDate(date.getDate() + days);
        setSelectedDate(date.toISOString().split('T')[0]);
    };

    const toggleMetric = (key: string) => {
        setActiveMetrics(prev => {
            const next = new Set(prev);
            if (next.has(key)) {
                next.delete(key);
            } else {
                next.add(key);
            }
            return next;
        });
    };

    // Prepara dados completos para 24 horas
    const chartData = useMemo(() => {
        if (!report) return [];

        const dataMap = new Map(report.hourly_data.map(d => [d.hour, d]));
        let cumImp = 0, cumRev = 0, cumClicks = 0;

        return Array.from({ length: 24 }, (_, hour) => {
            const data = dataMap.get(hour);
            const impressions = data?.impressions || 0;
            const revenue = data?.revenue || 0;
            const clicks = data?.clicks || 0;

            cumImp += impressions;
            cumRev += revenue;
            cumClicks += clicks;

            // Busca log de alteração dessa hora
            const changeLog = report.logs.find(l => l.hour === hour);

            return {
                hour,
                name: `${hour.toString().padStart(2, '0')}h`,
                impressions: viewMode === 'cumulative' ? cumImp : impressions,
                revenue: viewMode === 'cumulative' ? cumRev : revenue,
                clicks: viewMode === 'cumulative' ? cumClicks : clicks,
                ecpm: data?.ecpm || 0,
                pmr: data?.pmr || 0,
                ctr: data?.ctr || 0,
                hasChange: !!changeLog,
                changeLog: changeLog || null,
            };
        });
    }, [report, viewMode]);

    // Calcula domínios dos eixos
    const domains = useMemo(() => {
        if (!chartData.length) return { left: [0, 100], right: [0, 10], percent: [0, 100] };

        const maxImp = Math.max(...chartData.map(d => d.impressions), 1);
        const maxClicks = Math.max(...chartData.map(d => d.clicks), 1);
        const maxRev = Math.max(...chartData.map(d => d.revenue), 1);
        const maxEcpm = Math.max(...chartData.map(d => d.ecpm), 1);

        return {
            left: [0, Math.max(maxImp, maxClicks) * 1.1],
            right: [0, Math.max(maxRev, maxEcpm) * 1.1],
            percent: [0, 100],
        };
    }, [chartData]);

    if (!rule) return null;

    const CustomTooltip = ({ active, payload, label }: any) => {
        if (!active || !payload?.length) return null;

        const hourData = chartData.find(d => d.name === label);
        const changeLog = hourData?.changeLog;

        return (
            <div className="bg-white p-3 rounded-lg shadow-lg border border-gray-200 text-sm min-w-[200px]">
                <div className="font-semibold text-gray-900 mb-2 pb-2 border-b border-gray-100">
                    {label}
                </div>

                {/* Detalhes da alteração de preço */}
                {changeLog && (
                    <div className={`mb-3 p-2 rounded-lg ${
                        changeLog.action === 'scaled_up' ? 'bg-emerald-50 border border-emerald-200' :
                            changeLog.action === 'scaled_down' ? 'bg-red-50 border border-red-200' :
                                'bg-blue-50 border border-blue-200'
                    }`}>
                        <div className={`flex items-center gap-1.5 font-semibold text-xs mb-1 ${
                            changeLog.action === 'scaled_up' ? 'text-emerald-700' :
                                changeLog.action === 'scaled_down' ? 'text-red-700' :
                                    'text-blue-700'
                        }`}>
                            {changeLog.action === 'scaled_up' ? (
                                <><TrendingUp className="h-3 w-3" /> Aumento de Preço</>
                            ) : changeLog.action === 'scaled_down' ? (
                                <><TrendingDown className="h-3 w-3" /> Redução de Preço</>
                            ) : (
                                <><Zap className="h-3 w-3" /> {changeLog.action}</>
                            )}
                        </div>
                        <div className="text-xs text-gray-700">
                            <span className="font-medium">{formatPrice(changeLog.previous_price || 0)}</span>
                            <span className="mx-1">→</span>
                            <span className="font-bold">{formatPrice(changeLog.new_price || 0)}</span>
                            {changeLog.metadata?.scale_percentage && (
                                <span className={`ml-1 px-1 py-0.5 rounded text-[10px] font-medium ${
                                    changeLog.action === 'scaled_up' ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'
                                }`}>
                                    {changeLog.action === 'scaled_up' ? '+' : '-'}{changeLog.metadata.scale_percentage}%
                                </span>
                            )}
                        </div>
                        {changeLog.metadata?.pmr && (
                            <div className="text-[10px] text-gray-500 mt-1">
                                PMR: {changeLog.metadata.pmr.toFixed(1)}% | Meta: {changeLog.metadata.pmr_target?.toFixed(1)}%
                            </div>
                        )}
                    </div>
                )}

                {/* Métricas */}
                <div className="space-y-1">
                    {payload.map((entry: any, index: number) => (
                        <div key={index} className="flex items-center justify-between gap-4">
                            <div className="flex items-center gap-2">
                                <div className="w-2 h-2 rounded-full" style={{ backgroundColor: entry.color }} />
                                <span className="text-gray-600">{entry.name}</span>
                            </div>
                            <span className="font-medium text-gray-900">
                                {entry.dataKey === 'revenue' || entry.dataKey === 'ecpm'
                                    ? formatPrice(entry.value)
                                    : entry.dataKey === 'pmr' || entry.dataKey === 'ctr'
                                        ? `${entry.value.toFixed(1)}%`
                                        : formatNumber(entry.value)
                                }
                            </span>
                        </div>
                    ))}
                </div>
            </div>
        );
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-6xl max-h-[90vh] p-0 overflow-hidden">
                {/* Header */}
                <div className="px-6 py-4 border-b border-gray-100 bg-gradient-to-r from-blue-50 to-indigo-50">
                    <div className="flex items-center justify-between">
                        <div>
                            <h2 className="text-lg font-semibold text-gray-900">Relatório de Evolução</h2>
                            <div className="flex items-center gap-2 mt-1">
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-white/80 text-gray-700 text-xs rounded-md">
                                    <MapPin className="h-3 w-3 text-green-600" />
                                    {rule.country === '*' ? 'Todos' : rule.country}
                                </span>
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-white/80 text-gray-700 text-xs rounded-md">
                                    <Layers className="h-3 w-3 text-blue-600" />
                                    {rule.ad_unit === '*' ? 'Todos blocos' : rule.ad_unit}
                                </span>
                                {rule.custom_key && (
                                    <span
                                        className="inline-flex items-center gap-1 px-2 py-0.5 bg-orange-100 text-orange-700 text-xs rounded-md max-w-[250px]"
                                        title={rule.custom_key === 'id_post_wp' && rule.slug ? `#${rule.custom_value} /${rule.slug}` : `${rule.custom_key}=${rule.custom_value}`}
                                    >
                                        <Tag className="h-3 w-3 flex-shrink-0" />
                                        {rule.custom_key === 'id_post_wp' ? (
                                            <span className="truncate">#{rule.custom_value}{rule.slug && <span className="text-orange-500"> /{rule.slug}</span>}</span>
                                        ) : (
                                            <>{rule.custom_key}={rule.custom_value}</>
                                        )}
                                    </span>
                                )}
                            </div>
                        </div>
                        <div className="flex items-center gap-2">
                            <button onClick={() => changeDate(-1)} className="p-2 hover:bg-white/50 rounded-lg transition-colors">
                                <ChevronLeft className="h-4 w-4 text-gray-600" />
                            </button>
                            <div className="relative">
                                <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                                <input
                                    type="date"
                                    value={selectedDate}
                                    onChange={(e) => setSelectedDate(e.target.value)}
                                    className="h-9 pl-10 pr-3 border border-gray-200 rounded-lg text-sm bg-white focus:outline-none focus:border-blue-500"
                                />
                            </div>
                            <button onClick={() => changeDate(1)} className="p-2 hover:bg-white/50 rounded-lg transition-colors">
                                <ChevronRight className="h-4 w-4 text-gray-600" />
                            </button>
                            <button onClick={() => refetch()} className="p-2 hover:bg-white/50 rounded-lg transition-colors">
                                <RefreshCw className="h-4 w-4 text-gray-600" />
                            </button>
                        </div>
                    </div>
                </div>

                {/* Content */}
                <div className="p-6 overflow-y-auto max-h-[calc(90vh-80px)] space-y-5">
                    {isLoading ? (
                        <div className="flex items-center justify-center py-16">
                            <div className="flex items-center gap-3">
                                <div className="w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
                                <span className="text-gray-600">Carregando relatório...</span>
                            </div>
                        </div>
                    ) : !report ? (
                        <div className="text-center py-16">
                            <p className="text-gray-500">Não foi possível carregar o relatório</p>
                        </div>
                    ) : (
                        <>
                            {/* Summary Cards */}
                            <div className="grid grid-cols-3 md:grid-cols-6 gap-3">
                                <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-3 rounded-xl border border-blue-200">
                                    <div className="flex items-center gap-1.5 mb-1">
                                        <Eye className="h-3.5 w-3.5 text-blue-600" />
                                        <span className="text-xs text-blue-600 font-medium">Impressões</span>
                                    </div>
                                    <p className="text-xl font-bold text-blue-900">{formatNumber(report.summary.total_impressions)}</p>
                                </div>

                                <div className="bg-gradient-to-br from-amber-50 to-amber-100 p-3 rounded-xl border border-amber-200">
                                    <div className="flex items-center gap-1.5 mb-1">
                                        <DollarSign className="h-3.5 w-3.5 text-amber-600" />
                                        <span className="text-xs text-amber-600 font-medium">Receita</span>
                                    </div>
                                    <p className="text-xl font-bold text-amber-900">{formatPrice(report.summary.total_revenue)}</p>
                                </div>

                                <div className="bg-gradient-to-br from-emerald-50 to-emerald-100 p-3 rounded-xl border border-emerald-200">
                                    <div className="flex items-center gap-1.5 mb-1">
                                        <BarChart3 className="h-3.5 w-3.5 text-emerald-600" />
                                        <span className="text-xs text-emerald-600 font-medium">eCPM Médio</span>
                                    </div>
                                    <p className="text-xl font-bold text-emerald-900">{formatPrice(report.summary.avg_ecpm)}</p>
                                </div>

                                <div className="bg-gradient-to-br from-purple-50 to-purple-100 p-3 rounded-xl border border-purple-200">
                                    <div className="flex items-center gap-1.5 mb-1">
                                        <Percent className="h-3.5 w-3.5 text-purple-600" />
                                        <span className="text-xs text-purple-600 font-medium">PMR Médio</span>
                                    </div>
                                    <p className="text-xl font-bold text-purple-900">{report.summary.avg_pmr.toFixed(1)}%</p>
                                </div>

                                <div className="bg-gradient-to-br from-rose-50 to-rose-100 p-3 rounded-xl border border-rose-200">
                                    <div className="flex items-center gap-1.5 mb-1">
                                        <Zap className="h-3.5 w-3.5 text-rose-600" />
                                        <span className="text-xs text-rose-600 font-medium">Alterações</span>
                                    </div>
                                    <p className="text-xl font-bold text-rose-900">{report.summary.price_changes}</p>
                                    <div className="flex gap-2 text-[10px]">
                                        <span className="text-emerald-600 font-medium">↑{report.summary.scale_ups}</span>
                                        <span className="text-red-600 font-medium">↓{report.summary.scale_downs}</span>
                                    </div>
                                </div>

                                <div className="bg-gradient-to-br from-slate-50 to-slate-100 p-3 rounded-xl border border-slate-200">
                                    <div className="flex items-center gap-1.5 mb-1">
                                        <Clock className="h-3.5 w-3.5 text-slate-600" />
                                        <span className="text-xs text-slate-600 font-medium">Cobertura</span>
                                    </div>
                                    <p className="text-xl font-bold text-slate-900">{report.summary.hours_with_data}/24</p>
                                </div>
                            </div>

                            {/* Chart Controls */}
                            <div className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm">
                                <div className="flex items-center justify-between mb-4">
                                    <div className="flex items-center gap-3">
                                        <span className="text-sm font-medium text-gray-700">Métricas:</span>
                                        <span className="text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded">
                                            {activeMetrics.size}/{METRICS.length} ativas
                                        </span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <span className="text-xs text-gray-500">Visualização:</span>
                                        <div className="inline-flex rounded-lg border border-gray-200 p-0.5 bg-gray-100">
                                            <button
                                                onClick={() => setViewMode('hourly')}
                                                className={`px-3 py-1 text-xs font-medium rounded-md transition-all ${
                                                    viewMode === 'hourly'
                                                        ? 'bg-white text-gray-900 shadow-sm'
                                                        : 'text-gray-500 hover:text-gray-700'
                                                }`}
                                            >
                                                Hora a Hora
                                            </button>
                                            <button
                                                onClick={() => setViewMode('cumulative')}
                                                className={`px-3 py-1 text-xs font-medium rounded-md transition-all ${
                                                    viewMode === 'cumulative'
                                                        ? 'bg-white text-gray-900 shadow-sm'
                                                        : 'text-gray-500 hover:text-gray-700'
                                                }`}
                                            >
                                                Acumulado
                                            </button>
                                        </div>
                                    </div>
                                </div>

                                {/* Metric Toggles */}
                                <div className="flex flex-wrap gap-2 mb-4">
                                    {METRICS.map(metric => (
                                        <button
                                            key={metric.key}
                                            onClick={() => toggleMetric(metric.key)}
                                            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all border ${
                                                activeMetrics.has(metric.key)
                                                    ? 'border-transparent text-white shadow-sm'
                                                    : 'border-gray-300 text-gray-500 bg-white hover:bg-gray-50'
                                            }`}
                                            style={{
                                                backgroundColor: activeMetrics.has(metric.key) ? metric.color : undefined
                                            }}
                                        >
                                            <div
                                                className={`w-2 h-2 rounded-full ${!activeMetrics.has(metric.key) ? 'border border-current' : ''}`}
                                                style={{ backgroundColor: activeMetrics.has(metric.key) ? 'white' : metric.color }}
                                            />
                                            {metric.label}
                                        </button>
                                    ))}
                                </div>

                                {/* Chart */}
                                <div className="h-80">
                                    <ResponsiveContainer width="100%" height="100%">
                                        <LineChart data={chartData} margin={{ top: 5, right: 60, left: 20, bottom: 5 }}>
                                            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                                            <XAxis
                                                dataKey="name"
                                                tick={{ fontSize: 11, fill: '#6b7280' }}
                                                tickLine={false}
                                                axisLine={{ stroke: '#e5e7eb' }}
                                            />
                                            <YAxis
                                                yAxisId="left"
                                                orientation="left"
                                                tick={{ fontSize: 11, fill: '#6b7280' }}
                                                tickLine={false}
                                                axisLine={{ stroke: '#e5e7eb' }}
                                                tickFormatter={(v) => v >= 1000 ? `${(v/1000).toFixed(0)}k` : v}
                                                domain={domains.left}
                                            />
                                            <YAxis
                                                yAxisId="right"
                                                orientation="right"
                                                tick={{ fontSize: 11, fill: '#6b7280' }}
                                                tickLine={false}
                                                axisLine={{ stroke: '#e5e7eb' }}
                                                tickFormatter={(v) => `$${v.toFixed(0)}`}
                                                domain={domains.right}
                                            />
                                            <YAxis
                                                yAxisId="percent"
                                                orientation="right"
                                                tick={{ fontSize: 11, fill: '#6b7280' }}
                                                tickLine={false}
                                                axisLine={false}
                                                tickFormatter={(v) => `${v}%`}
                                                domain={[0, 100]}
                                                hide
                                            />
                                            <Tooltip content={<CustomTooltip />} />

                                            {/* Reference lines for price changes */}
                                            {report.logs.map((log, i) => (
                                                <ReferenceLine
                                                    key={i}
                                                    x={`${log.hour.toString().padStart(2, '0')}h`}
                                                    stroke="#f59e0b"
                                                    strokeDasharray="4 4"
                                                    strokeWidth={1}
                                                />
                                            ))}

                                            {/* Lines */}
                                            {METRICS.map((metric) => (
                                                activeMetrics.has(metric.key) && (
                                                    <Line
                                                        key={metric.key}
                                                        yAxisId={metric.yAxisId}
                                                        type="monotone"
                                                        dataKey={metric.key}
                                                        name={metric.label}
                                                        stroke={metric.color}
                                                        strokeWidth={2}
                                                        dot={(props: any) => {
                                                            const { cx, cy, payload } = props;
                                                            // Ponto normal
                                                            const normalDot = (
                                                                <circle
                                                                    cx={cx}
                                                                    cy={cy}
                                                                    r={3}
                                                                    fill={metric.color}
                                                                    key={`dot-${metric.key}-${payload?.hour}`}
                                                                />
                                                            );

                                                            // Se tem alteração e é a primeira métrica ativa, mostra ponto destacado
                                                            const isFirstActive = METRICS.filter(m => activeMetrics.has(m.key))[0]?.key === metric.key;
                                                            if (payload?.hasChange && isFirstActive) {
                                                                return (
                                                                    <g key={`change-${payload?.hour}`}>
                                                                        {normalDot}
                                                                        <circle cx={cx} cy={cy} r={12} fill="#f59e0b" opacity={0.2} />
                                                                        <circle cx={cx} cy={cy} r={7} fill="#f59e0b" stroke="white" strokeWidth={2} />
                                                                    </g>
                                                                );
                                                            }
                                                            return normalDot;
                                                        }}
                                                        activeDot={{ r: 5, fill: metric.color, strokeWidth: 2, stroke: 'white' }}
                                                    />
                                                )
                                            ))}
                                        </LineChart>
                                    </ResponsiveContainer>
                                </div>

                                {/* Legend */}
                                <div className="flex flex-wrap justify-center gap-4 mt-4 pt-4 border-t border-gray-100">
                                    {METRICS.filter(m => activeMetrics.has(m.key)).map(metric => (
                                        <div key={metric.key} className="flex items-center gap-1.5 text-xs text-gray-600">
                                            <div className="w-3 h-0.5 rounded" style={{ backgroundColor: metric.color }} />
                                            {metric.label}
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* Logs + Current Info */}
                            <div className="grid gap-4 md:grid-cols-3">
                                {/* Price Changes */}
                                <div className="md:col-span-2 bg-white p-4 rounded-xl border border-gray-200 shadow-sm">
                                    <h3 className="text-sm font-semibold text-gray-800 mb-3 flex items-center gap-2">
                                        <div className="p-1.5 bg-amber-100 rounded-lg">
                                            <Zap className="h-4 w-4 text-amber-600" />
                                        </div>
                                        Alterações de Preço
                                    </h3>

                                    {report.logs.length === 0 ? (
                                        <div className="text-center py-8 bg-gray-50 rounded-lg">
                                            <Minus className="h-8 w-8 text-gray-300 mx-auto" />
                                            <p className="text-sm text-gray-500 mt-2">Nenhuma alteração no período</p>
                                        </div>
                                    ) : (
                                        <div className="space-y-2 max-h-[200px] overflow-y-auto">
                                            {report.logs.map((log) => (
                                                <div
                                                    key={log.id}
                                                    className={`p-3 rounded-lg border-l-4 ${
                                                        log.action === 'scaled_up'
                                                            ? 'bg-emerald-50 border-emerald-500'
                                                            : log.action === 'scaled_down'
                                                                ? 'bg-red-50 border-red-500'
                                                                : 'bg-blue-50 border-blue-500'
                                                    }`}
                                                >
                                                    <div className="flex items-center justify-between">
                                                        <div className="flex items-center gap-3">
                                                            <span className={`inline-flex items-center gap-1 text-xs font-semibold ${
                                                                log.action === 'scaled_up' ? 'text-emerald-700' :
                                                                    log.action === 'scaled_down' ? 'text-red-700' : 'text-blue-700'
                                                            }`}>
                                                                {log.action === 'scaled_up' ? (
                                                                    <><TrendingUp className="h-3 w-3" /> Aumento</>
                                                                ) : log.action === 'scaled_down' ? (
                                                                    <><TrendingDown className="h-3 w-3" /> Redução</>
                                                                ) : (
                                                                    <><Zap className="h-3 w-3" /> {log.action}</>
                                                                )}
                                                            </span>
                                                            <div className="flex items-center gap-1.5 text-sm">
                                                                <span className="font-medium text-gray-600">{formatPrice(log.previous_price || 0)}</span>
                                                                <span className="text-gray-400">→</span>
                                                                <span className="font-bold text-gray-900">{formatPrice(log.new_price || 0)}</span>
                                                                {log.metadata?.scale_percentage && (
                                                                    <span className={`text-xs px-1.5 py-0.5 rounded font-medium ${
                                                                        log.action === 'scaled_up' ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'
                                                                    }`}>
                                                                        {log.action === 'scaled_up' ? '+' : '-'}{log.metadata.scale_percentage}%
                                                                    </span>
                                                                )}
                                                            </div>
                                                        </div>
                                                        <span className="text-xs text-gray-500 font-medium">
                                                            {new Date(log.created_at).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                                                        </span>
                                                    </div>
                                                    {log.metadata?.pmr && (
                                                        <div className="mt-2 text-xs text-gray-500 flex gap-3">
                                                            <span>PMR: <strong>{log.metadata.pmr.toFixed(1)}%</strong></span>
                                                            <span>Meta: <strong>{log.metadata.pmr_target?.toFixed(1)}%</strong></span>
                                                        </div>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>

                                {/* Current State */}
                                <div className="bg-gradient-to-br from-slate-50 to-slate-100 p-4 rounded-xl border border-slate-200">
                                    <h3 className="text-sm font-semibold text-slate-800 mb-3">Estado Atual</h3>
                                    <div className="space-y-3">
                                        <div className="bg-white p-3 rounded-lg shadow-sm">
                                            <p className="text-xs text-gray-500 mb-1">Price Atual</p>
                                            <p className="text-2xl font-bold text-gray-900">{formatPrice(rule.price)}</p>
                                        </div>
                                        <div className="grid grid-cols-2 gap-2">
                                            <div className="bg-white p-2 rounded-lg shadow-sm">
                                                <p className="text-[10px] text-gray-500">PMR</p>
                                                <p className="text-sm font-bold text-gray-900">{rule.current_pmr?.toFixed(1) || '-'}%</p>
                                            </div>
                                            <div className="bg-white p-2 rounded-lg shadow-sm">
                                                <p className="text-[10px] text-gray-500">eCPM</p>
                                                <p className="text-sm font-bold text-gray-900">{rule.current_ecpm ? formatPrice(rule.current_ecpm) : '-'}</p>
                                            </div>
                                        </div>
                                        <div className="bg-white p-2 rounded-lg shadow-sm">
                                            <p className="text-[10px] text-gray-500">Meta PMR</p>
                                            <p className="text-sm font-bold text-gray-900">{rule.pmr_target}%</p>
                                        </div>
                                        <div className={`p-2 rounded-lg text-center ${rule.status === 'active' ? 'bg-emerald-500' : 'bg-gray-400'}`}>
                                            <span className="text-xs font-semibold text-white">
                                                {rule.status === 'active' ? '● Regra Ativa' : '○ Regra Inativa'}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Hourly Table */}
                            <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                                <div className="px-4 py-3 border-b border-gray-100 bg-gray-50 flex items-center justify-between">
                                    <h3 className="text-sm font-semibold text-gray-800 flex items-center gap-2">
                                        <Clock className="h-4 w-4 text-gray-600" />
                                        Dados por Hora
                                    </h3>
                                </div>
                                <div className="overflow-x-auto max-h-[300px]">
                                    <table className="min-w-full text-sm">
                                        <thead className="bg-gray-50 sticky top-0">
                                        <tr>
                                            <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-600">Hora</th>
                                            <th className="px-4 py-2.5 text-right text-xs font-semibold text-gray-600">Impressões</th>
                                            <th className="px-4 py-2.5 text-right text-xs font-semibold text-gray-600">Receita</th>
                                            <th className="px-4 py-2.5 text-right text-xs font-semibold text-gray-600">eCPM</th>
                                            <th className="px-4 py-2.5 text-right text-xs font-semibold text-gray-600">PMR</th>
                                            <th className="px-4 py-2.5 text-right text-xs font-semibold text-gray-600">Clicks</th>
                                            <th className="px-4 py-2.5 text-right text-xs font-semibold text-gray-600">CTR</th>
                                        </tr>
                                        </thead>
                                        <tbody className="divide-y divide-gray-100">
                                        {chartData.map((data) => {
                                            const hasData = data.impressions > 0 || (viewMode === 'cumulative' && data.hour > 0);
                                            return (
                                                <tr key={data.hour} className={`${data.hasChange ? 'bg-amber-50' : hasData ? 'hover:bg-gray-50' : 'bg-gray-50/50'} transition-colors`}>
                                                    <td className="px-4 py-2 font-mono text-xs font-medium text-gray-900">
                                                        {data.name}
                                                        {data.hasChange && <Zap className="h-3 w-3 text-amber-500 inline ml-1.5" />}
                                                    </td>
                                                    <td className={`px-4 py-2 text-right ${hasData ? 'text-gray-700 font-medium' : 'text-gray-400'}`}>
                                                        {hasData ? formatNumber(data.impressions) : '-'}
                                                    </td>
                                                    <td className={`px-4 py-2 text-right ${hasData ? 'text-gray-700' : 'text-gray-400'}`}>
                                                        {hasData ? formatPrice(data.revenue) : '-'}
                                                    </td>
                                                    <td className={`px-4 py-2 text-right ${hasData && data.ecpm > 0 ? 'font-semibold text-gray-900' : 'text-gray-400'}`}>
                                                        {hasData && data.ecpm > 0 ? formatPrice(data.ecpm) : '-'}
                                                    </td>
                                                    <td className="px-4 py-2 text-right">
                                                        {hasData && data.pmr > 0 ? (
                                                            <span className={`font-semibold px-1.5 py-0.5 rounded text-xs ${
                                                                data.pmr >= rule.pmr_target
                                                                    ? 'bg-emerald-100 text-emerald-700'
                                                                    : 'bg-red-100 text-red-700'
                                                            }`}>
                                                                    {data.pmr.toFixed(1)}%
                                                                </span>
                                                        ) : (
                                                            <span className="text-gray-400">-</span>
                                                        )}
                                                    </td>
                                                    <td className={`px-4 py-2 text-right ${hasData ? 'text-gray-600' : 'text-gray-400'}`}>
                                                        {hasData ? data.clicks : '-'}
                                                    </td>
                                                    <td className={`px-4 py-2 text-right ${hasData && data.ctr > 0 ? 'text-gray-600' : 'text-gray-400'}`}>
                                                        {hasData && data.ctr > 0 ? `${data.ctr.toFixed(2)}%` : '-'}
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </>
                    )}
                </div>
            </DialogContent>
        </Dialog>
    );
}